import React, { useEffect, useState } from "react";
import { authFetch } from "../../api";
import { FiTrash2, FiPlus } from "react-icons/fi";

const inputClass = "px-3 py-1.5 rounded-md bg-white/5 border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light";
const selectClass = "px-3 py-1.5 rounded-md bg-navy-dark border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light";
const optionClass = "bg-navy-dark text-white";

const levelLabels = ["Main Menu", "Sub Menu", "Sub-sub Menu"];

// Depth-first ordering so children are grouped right under their parent, with a `level` for indentation.
function flattenWithLevel(items) {
  const byParent = {};
  items.forEach((item) => {
    const key = item.parent || "root";
    (byParent[key] = byParent[key] || []).push(item);
  });
  Object.values(byParent).forEach((list) => list.sort((a, b) => a.order - b.order));

  const result = [];
  const walk = (parentKey, level) => {
    (byParent[parentKey] || []).forEach((item) => {
      result.push({ ...item, level });
      walk(item.id, level + 1);
    });
  };
  walk("root", 0);
  return result;
}

function getDescendantIds(items, id) {
  const ids = new Set();
  const walk = (parentId) => {
    items.filter((i) => i.parent === parentId).forEach((child) => {
      ids.add(child.id);
      walk(child.id);
    });
  };
  walk(id);
  return ids;
}

export default function NavbarSettings() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [newItem, setNewItem] = useState({ title: "", slug: "", parent: "" });

  const [buttons, setButtons] = useState(null);
  const [buttonsMessage, setButtonsMessage] = useState("");
  const [buttonsError, setButtonsError] = useState("");

  const load = () => {
    authFetch("/core/menu/")
      .then((res) => res.json())
      .then(setItems)
      .catch(() => setError("Failed to load menu items."));
  };

  useEffect(load, []);

  useEffect(() => {
    authFetch("/core/navbar/")
      .then((res) => res.json())
      .then(setButtons)
      .catch(() => setButtonsError("Failed to load navbar buttons."));
  }, []);

  const saveButtons = async (e) => {
    e.preventDefault();
    setButtonsMessage("");
    setButtonsError("");
    try {
      const res = await authFetch("/core/navbar/", { method: "PATCH", body: JSON.stringify(buttons) });
      if (!res.ok) throw new Error();
      setButtonsMessage("Navbar buttons updated.");
    } catch {
      setButtonsError("Failed to save.");
    }
  };

  const updateField = (id, field, value) => {
    setItems(items.map((it) => (it.id === id ? { ...it, [field]: value } : it)));
  };

  const saveItem = async (item) => {
    setError("");
    const res = await authFetch(`/core/menu/${item.id}/`, {
      method: "PATCH",
      body: JSON.stringify({ ...item, parent: item.parent || null }),
    });
    if (!res.ok) {
      const data = await res.json();
      setError(data.parent?.[0] || "Failed to save item.");
      return;
    }
    load();
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this menu item and all of its sub-items?")) return;
    await authFetch(`/core/menu/${id}/`, { method: "DELETE" });
    load();
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!newItem.title) return;
    setError("");
    const res = await authFetch("/core/menu/", {
      method: "POST",
      body: JSON.stringify({
        title: newItem.title,
        slug: newItem.slug || newItem.title.toLowerCase().replace(/\s+/g, "-"),
        parent: newItem.parent || null,
        order: items.length + 1,
        is_active: true,
      }),
    });
    if (!res.ok) {
      const data = await res.json();
      setError(data.parent?.[0] || data.slug?.[0] || "Failed to add item.");
      return;
    }
    setNewItem({ title: "", slug: "", parent: "" });
    load();
  };

  const rows = flattenWithLevel(items);

  const parentOptionsFor = (item) => {
    const excluded = item ? getDescendantIds(items, item.id) : new Set();
    if (item) excluded.add(item.id);
    return rows.filter((r) => r.level < 2 && !excluded.has(r.id));
  };

  return (
    <div className="w-full">
      <h1 className="text-2xl font-bold mb-2">Navbar Menu</h1>
      <p className="text-sm text-gray-400 mb-6">
        Build up to 3 levels: Main Menu → Sub Menu → Sub-sub Menu. Set a parent to nest an item.
      </p>

      <div className="bg-navy border border-navy-border rounded-xl p-6 mb-8">
        <h2 className="text-lg font-semibold mb-4">Login &amp; Get Quote Buttons</h2>
        {!buttons ? (
          <p className="text-gray-400 text-sm">Loading...</p>
        ) : (
          <form onSubmit={saveButtons} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Login Text</label>
                <input
                  className={inputClass}
                  value={buttons.login_text}
                  onChange={(e) => setButtons({ ...buttons, login_text: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Login URL</label>
                <input
                  className={inputClass}
                  value={buttons.login_url}
                  onChange={(e) => setButtons({ ...buttons, login_url: e.target.value })}
                  placeholder="/login or #  or https://..."
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={buttons.login_visible}
                  onChange={(e) => setButtons({ ...buttons, login_visible: e.target.checked })}
                />
                Visible
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Get Quote Text</label>
                <input
                  className={inputClass}
                  value={buttons.quote_text}
                  onChange={(e) => setButtons({ ...buttons, quote_text: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Get Quote address (empty = opens the quote window)</label>
                <input
                  className={inputClass}
                  value={buttons.quote_url}
                  onChange={(e) => setButtons({ ...buttons, quote_url: e.target.value })}
                  placeholder="Leave empty to open the quote window - or enter /contact or https://..."
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={buttons.quote_visible}
                  onChange={(e) => setButtons({ ...buttons, quote_visible: e.target.checked })}
                />
                Visible
              </label>
            </div>

            {buttonsMessage && <p className="text-sm text-green-400">{buttonsMessage}</p>}
            {buttonsError && <p className="text-sm text-red-400">{buttonsError}</p>}
            <button className="px-5 py-2 rounded-md bg-brand-gradient text-white text-sm font-semibold">
              Save Buttons
            </button>
          </form>
        )}
      </div>

      {error && <p className="text-red-400 mb-4">{error}</p>}

      <div className="bg-navy border border-navy-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-gray-400 text-left">
            <tr>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Slug</th>
              <th className="px-4 py-3">Parent</th>
              <th className="px-4 py-3 w-20">Order</th>
              <th className="px-4 py-3 w-24">Active</th>
              <th className="px-4 py-3 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => (
              <tr key={item.id} className="border-t border-navy-border">
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2" style={{ paddingLeft: `${item.level * 20}px` }}>
                    {item.level > 0 && <span className="text-gray-500">↳</span>}
                    <input
                      className={inputClass}
                      value={item.title}
                      onChange={(e) => updateField(item.id, "title", e.target.value)}
                    />
                    <span className="text-[10px] uppercase tracking-wide text-gray-500 whitespace-nowrap">
                      {levelLabels[item.level]}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-2">
                  <input
                    className={inputClass}
                    value={item.slug}
                    onChange={(e) => updateField(item.id, "slug", e.target.value)}
                  />
                </td>
                <td className="px-4 py-2">
                  <select
                    className={selectClass}
                    value={item.parent || ""}
                    onChange={(e) => updateField(item.id, "parent", e.target.value ? Number(e.target.value) : null)}
                  >
                    <option className={optionClass} value="">— Main Menu (no parent) —</option>
                    {parentOptionsFor(item).map((opt) => (
                      <option className={optionClass} key={opt.id} value={opt.id}>
                        {"— ".repeat(opt.level)}{opt.title}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-2">
                  <input
                    type="number"
                    className={`${inputClass} w-16`}
                    value={item.order}
                    onChange={(e) => updateField(item.id, "order", Number(e.target.value))}
                  />
                </td>
                <td className="px-4 py-2">
                  <input
                    type="checkbox"
                    checked={item.is_active}
                    onChange={(e) => updateField(item.id, "is_active", e.target.checked)}
                  />
                </td>
                <td className="px-4 py-2 flex gap-2">
                  <button
                    onClick={() => saveItem(item)}
                    className="px-3 py-1 rounded-md bg-brand-gradient text-white text-xs font-semibold"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => deleteItem(item.id)}
                    className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10"
                  >
                    <FiTrash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form onSubmit={addItem} className="mt-6 flex gap-3 items-end flex-wrap">
        <div>
          <label className="block text-xs text-gray-400 mb-1">New item title</label>
          <input
            className={inputClass}
            value={newItem.title}
            onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
            placeholder="e.g. Portfolio"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Slug (optional)</label>
          <input
            className={inputClass}
            value={newItem.slug}
            onChange={(e) => setNewItem({ ...newItem, slug: e.target.value })}
            placeholder="portfolio"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Parent</label>
          <select
            className={selectClass}
            value={newItem.parent}
            onChange={(e) => setNewItem({ ...newItem, parent: e.target.value })}
          >
            <option className={optionClass} value="">— Main Menu (no parent) —</option>
            {parentOptionsFor(null).map((opt) => (
              <option className={optionClass} key={opt.id} value={opt.id}>
                {"— ".repeat(opt.level)}{opt.title}
              </option>
            ))}
          </select>
        </div>
        <button className="px-4 py-2 rounded-md bg-brand-gradient text-white text-sm font-semibold flex items-center gap-2">
          <FiPlus /> Add
        </button>
      </form>
    </div>
  );
}
